import { Injectable } from '@angular/core';
import { OccasionDate, PlayableResourceSpec, Resource } from './resource.model';
import { User } from '@klickdata/core/user';
import { ResourceItem } from '@klickdata/core/resource-item';
import { ResourceTypes } from './types.enum';
import * as moment from 'moment';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { takeUntil, filter, switchMap, map, of, Observable, BehaviorSubject, first, tap, EMPTY, merge } from 'rxjs';
import { ResourceService } from './resource.service';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@klickdata/core/auth';
import { GuestService } from '@klickdata/guest/src/guest.service';
import { Utils } from '@klickdata/core/util';

@Injectable({
    providedIn: 'root',
})
export class StartResourceService extends OnDestroyHandler {
    public resource: BehaviorSubject<Resource> = new BehaviorSubject<Resource>(null);
    constructor(
        protected mobile: MobileService,
        protected resourceService: ResourceService,
        protected router: Router,
        protected location: Location,
        protected guestService: GuestService,
        protected dialog: MatDialog,
        protected message: MessageService,
        protected auth: AuthService
    ) {
        super();
    }

    public takeResource(resource: Resource, user: User, item?: ResourceItem): Observable<Resource> {
        if (ResourceTypes.parentType(resource.type_id) == ResourceTypes.SCORM && resource.prompt_status == 'ERROR') {
            this.mobile.updateSideNavSub({
                dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
                data: {
                    icon: 'block',
                    title: $localize`Can't play SCORM`,
                    contentBody: resource.prompt_error
                        ? $localize`This SCORM is not ready yet (${resource.prompt_error}).`
                        : $localize`You can't play this SCORM.`,
                    positiveBtn: $localize`OK`,
                },
            });
            return EMPTY;
        } else {
            return this.handleEnrollment(resource, item);
        }
    }
    private handleEnrollment(resource: Resource, item?: ResourceItem): Observable<Resource> {
        const resourceType = ResourceTypes.parentType(resource.type_id);
        const typeRequiresItems = ResourceTypes.isTypeThatHasItems(resourceType);
        const hasNoItems = !resource.number_of_items;

        if (hasNoItems && typeRequiresItems) {
            const resourceLabel = ResourceTypes.label(resource.type_id, { capitalize: false });
            this.mobile.updateSideNavSub({
                dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
                data: {
                    icon: 'block',
                    title: $localize`Can't play ${resourceLabel}`,
                    contentBody: $localize`You can't play this ${resourceLabel} because it has no items`,
                    positiveBtn: $localize`OK`,
                },
            });
            return EMPTY;
        }
        if (resource.ready_play) {
            const resourceLabel = ResourceTypes.label(resource.type_id, { capitalize: false });
            this.mobile.updateSideNavSub({
                dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
                data: {
                    icon: 'block',
                    title: $localize`Can't play ${resourceLabel}`,
                    contentBody: $localize`You can't play this ${resourceLabel} because it's not ready for play due to hidden items`,
                    positiveBtn: $localize`OK`,
                },
            });
            return EMPTY;
        }
        if (resource.opportunity_id) {
            return this.askForEnroll(resource, false, null);
        }
        const obs =
            resource.user_collaboration && resource.user_enrollment
                ? of(resource)
                : resource.user_collaboration
                ? this.resourceService
                      .getResource(resource.id, 'user_enrollment')
                      .pipe(tap((res) => (res.user_collaboration = resource.user_collaboration)))
                : resource.user_enrollment
                ? this.resourceService
                      .getResource(resource.id, 'user_collaboration')
                      .pipe(tap((res) => (res.user_enrollment = resource.user_enrollment)))
                : this.resourceService.getResource(resource.id, 'user_enrollment,user_collaboration');

        return obs.pipe(
            first(),
            switchMap((resource) => {
                const enroll_approval_required = resource.enrollmentRequiredStatus;
                if (enroll_approval_required && resource.user_enrollment) {
                    if (
                        resource.user_enrollment.approval_status == 'rejected' ||
                        resource.user_enrollment.approval_status == 'pending' ||
                        resource.user_enrollment.approval_status == 'waiting'
                    ) {
                        this.informApprovalStatus(resource);
                        return of(resource);
                    } else if (
                        resource.user_enrollment.approval_status === 'approved' ||
                        resource.user_enrollment.approval_status === 'enrolled'
                    ) {
                        return this.onAdminApproved(resource, item);
                    }
                } else {
                    return this.askForEnroll(resource, enroll_approval_required, item);
                }
            })
        );
    }

    private onAdminApproved(resource: Resource, item?: ResourceItem): Observable<Resource> {
        const preventEnrollUntillOccasion = this.isPreventEnrollUntillOccasionDate(resource);

        const msgBody = resource.user_enrollment.admin?.message?.body;
        // const preventEnrollUntill = this.getPreventEnrollUntillMsg(resource);
        const resourceType = ResourceTypes.label(resource.type_id, { capitalize: false });

        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'task_alt',
                iconClass: 'green',
                titleClass: 'green',
                title: preventEnrollUntillOccasion
                    ? $localize`Enrollment Request approved (Enrollment is not available yet)`
                    : $localize`Enrollment Request approved`,
                contentBody: this.getApplyingInfo(resource),
                // contentBody: preventEnrollUntillOccasion
                //     ? $localize`The administrator has approved your request to enroll in the ${resourceType} ${resource.title} \n But the enrollment is not available yet`
                //     : $localize`The administrator has approved your request to enroll in the ${resourceType} ${resource.title} \n Good luck !`,
                secContentBody: msgBody
                    ? '<span>' + $localize`The administrator message: ` + `</span><span>${msgBody}</span>`
                    : null,
                secContentClass: msgBody ? 'reviewer-msg' : null,
                positiveBtn: $localize`Start `,
                negativeBtn: $localize`Cancel`,
            },
        });

        return this.mobile.getSideNavAction().pipe(
            filter((action) => action === SideNaveActionsTypes.POSITIVE && !preventEnrollUntillOccasion),
            takeUntil(this.destroy),
            tap(() => this.startResource(resource, item)),
            map(() => resource)
        );
    }
    private askForEnrollWhenWaiting(
        resource: Resource,
        enroll_approval_required: boolean,
        item: ResourceItem
    ): Observable<Resource> {
        return this.askForEnroll(
            resource,
            enroll_approval_required,
            item,
            resource.user_enrollment.metadata.occasion_id
        );
    }
    private isPreventEnrollUntillOccasionDate(resource: Resource): boolean {
        const occasionDates = resource.user_collaboration.enroll_approval_required?.metadata?.occasionDates;
        if (!occasionDates?.length) return false;

        const preventEnrollUntillOccasion =
            occasionDates.some(
                (occasion) => !moment().isBetween(occasion.start_date, occasion.end_date, 'day', '[]')
            ) ||
            occasionDates.some((occasion) => moment().isBefore(occasion.start_date)) ||
            occasionDates.some((occasion) => moment().isAfter(occasion.end_date));
        return preventEnrollUntillOccasion;
    }
    private getPreventEnrollUntillMsg(resource: Resource): string {
        const occasionDates = resource.user_collaboration.enroll_approval_required?.metadata?.occasionDates;
        if (!occasionDates?.length) return '';

        const startDates = occasionDates.map((date) => moment(date.start_date).format('DD/MM/YYYY'));

        if (startDates.length === 1) {
            return $localize`The enrollment starts ${startDates[0]}`;
        }

        const formattedDates = startDates.join(', ');
        return $localize`The enrollment starts on the following dates: ${formattedDates}`;
    }
    private informApprovalStatus(resource: Resource) {
        const msgBody = resource.user_enrollment.admin?.message?.body;

        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: resource.user_enrollment.approval_status == 'rejected' ? 'cancel' : 'pending_actions',
                iconClass: resource.user_enrollment.approval_status == 'rejected' ? 'red' : 'orange',
                titleClass: resource.user_enrollment.approval_status == 'rejected' ? 'red' : 'orange',
                title:
                    resource.user_enrollment.approval_status == 'rejected'
                        ? $localize`Enrollment Request rejected`
                        : resource.user_enrollment.approval_status == 'waiting'
                        ? $localize`Enrollment Request in waiting list`
                        : $localize`Enrollment Request pending`,
                contentBody: this.getApplyingInfo(resource),

                secContentBody: msgBody
                    ? '<span>' + $localize`The administrator message: ` + `</span><span>${msgBody}</span>`
                    : null,
                secContentClass: msgBody ? 'reviewer-msg' : null,
                positiveBtn: $localize`Ok`,
                negativeBtn: $localize`Cancel`,
            },
        });
    }
    private getOccasionDate(resource: Resource): OccasionDate {
        const dates = resource.user_collaboration?.enroll_approval_required?.metadata?.occasion_dates;
        if (dates?.length) {
            return dates.find((date) => date.id === resource.user_enrollment.metadata.occasion_id);
        }
    }
    private getApplyingInfo(resource: Resource): string {
        const occasionDate = this.getOccasionDate(resource);
        if (!occasionDate) return '';
        const appliedAt = moment(resource.user_enrollment.requested_at).format('MMM D, YYYY h:mm a');
        const startDate = moment(occasionDate.start_date).format('MMM D, YYYY h:mm a');
        const endDate = moment(occasionDate.end_date).format('MMM D, YYYY h:mm a');
        const resourceLabel = ResourceTypes.label(resource.type_id, { capitalize: false });
        return (
            '<div class="applying-info">' +
            '<div class="applying-date">' +
            $localize`You applied on ` +
            `<strong class="applying-date">${appliedAt}</strong>` +
            '</div>' +
            '<div class="resource-info">' +
            $localize` to ` +
            `<span class="resource-info">${resourceLabel} ${resource.title}</span>` +
            '</div>' +
            '<div class="occasion-dates">' +
            $localize` on occasion ` +
            `<strong class="occasion-dates">${startDate} - ${endDate}</strong>` +
            '</div>' +
            '</div>'
        );
    }
    public askForEnroll(
        resource: Resource,
        approvalRequired: boolean,
        item?: ResourceItem,
        enrolledOccId?: number
    ): Observable<Resource> {
        const resourceNotTakenYet =
            !resource.opportunity_id ||
            (resource?.occasionStatus && !['ongoing', 'completed'].includes(resource?.occasionStatus));
        const actions = [{ label: $localize`Cancel`, value: 0, class: 'null-btn' }];
        actions.unshift(
            (approvalRequired && resourceNotTakenYet) ||
                (!approvalRequired &&
                    !!resource.enrollmentRequiredMetadata?.occasion_dates?.filter((occ) => !occ.disabled)?.length)
                ? { label: $localize`Request join`, value: 1, class: 'pos-btn' }
                : {
                      label: resourceNotTakenYet ? $localize`Start` : $localize`Continue`,
                      value: 2,
                      class: 'pos-btn',
                  }
        );
        const canEdit = resource.user_collaboration?.edit?.status;
        if (canEdit) {
            actions.push({ label: $localize`Edit`, value: 10, class: 'edit-btn' });
        }
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.RESOURCE_ENROLLMENT_REQUEST,
            data: {
                icon:
                    approvalRequired ||
                    (!approvalRequired &&
                        !!resource.enrollmentRequiredMetadata?.occasion_dates?.filter((occ) => !occ.disabled)?.length)
                        ? 'app_registration'
                        : resource.opportunity_id
                        ? 'double_arrow'
                        : 'play_circle',
                title: resourceNotTakenYet
                    ? approvalRequired ||
                      (!approvalRequired &&
                          !!resource.enrollmentRequiredMetadata?.occasion_dates?.filter((occ) => !occ.disabled)?.length)
                        ? $localize`Enroll`
                        : $localize`Start`
                    : $localize`Continue`,
                inputDesc: approvalRequired ? $localize`Send message to admin requesting to join` : null,
                descs: [this.getCompetenceClosedDesc(resource)],
                resource: resource,
                enrolledOccId: enrolledOccId,
                actions: actions,
                type: 'enrollRequest',
            },
        });

        return this.mobile.getSideNavResponseData().pipe(
            first(),
            filter((data) => SideNaveActionsTypes.isValid(data) || (data.type === 'enrollRequest' && data.response)),
            switchMap((data) => {
                if (data && data.response == 10) {
                    const queryParams: any = {};
                    queryParams.context = 'occasions';

                    if (resource.type_id === ResourceTypes.GeneralCoursePlan) {
                        this.router.navigate(['/admin/content/courses/', resource.id], { queryParams });
                    } else {
                        this.router.navigate(
                            [`/admin/content/${ResourceTypes.playerRoute(resource.type_id)}s/`, resource.id],
                            { queryParams }
                        );
                    }
                    return of(null);
                }
                if (SideNaveActionsTypes.isValid(data) && data !== SideNaveActionsTypes.POSITIVE) {
                    return of(null);
                } else {
                    return data.response == 1
                        ? this.resourceService.userRequestPermission(resource.id, {
                              message: { body: data.value },
                              selectedOccasionId: data.selectedOccasionId,
                              eager: ['user_enrollment'],
                          })
                        : of(data);
                }
            }),
            tap((data) => {
                if (data && data.response == 2) {
                    this.startResource(resource, item);
                }
                if (data instanceof Resource) {
                    this.resource.next(data);
                    this.message.openMessage(MessageSavedComponent, $localize`Request sent to the admin.`);
                }
            }),
            map((data) => (data && data.response == 2 ? null : data))
        );
    }

    public startResource(resource: Resource, item?: ResourceItem) {
        let queryParams: any = {
            referrer: this.location.path().split('?')[0],
            ...(item && { position: item.play_position }),
        };
        if (resource.user_enrollment?.metadata?.occasion_date_id) {
            queryParams = { ...queryParams, occasionDateId: resource.user_enrollment.metadata.occasion_id };
        }
        this.router.navigate(['/player/resource', resource.id, ResourceTypes.playerRoute(resource.type_id)], {
            queryParams,
        });
    }
    private registerEvent(res: Resource, user: User) {
        if (res.price) {
            this.mobile.updateSideNavSub({
                dataType: SideNaveDataTypes.EVENT_REGISTER,
                data:
                    user.role_value === 'guest'
                        ? {
                              resource: res,
                          }
                        : {
                              resource: res,
                              user: user,
                          },
            });
        } else {
            this.mobile.updateSideNavSub({
                dataType: SideNaveDataTypes.COURSE_REGISTER,
                data:
                    user.role_value === 'guest'
                        ? {
                              resource: res,
                          }
                        : {
                              resource: res,
                              user: user,
                          },
            });
        }
    }
    getResource(): Observable<Resource> {
        return this.resource.asObservable();
    }
    private getCompetenceClosedDesc(resource: Resource): string {
        return Utils.isResourceHasUserCompetenceRecurringExpiry(resource.user_competence);
    }
}
